import { RecordVoiceOverOutlined, TuneOutlined } from '@mui/icons-material';

import { Form, FormItem, Input } from '../../../../../../../components';
import { KeyValueList } from './KeyValueList';
import { StringList } from './StringList';
import { MetadataSectionCard } from './MetadataSectionCard';
import {
  DEFAULT_METADATA_KEYS,
  LOCKED_METADATA_KEYS,
  METADATA_KEY_VALIDATION,
  getMetadataFieldLabel,
} from '../constants';
import type { EditMetadataFormController } from '../hooks/useEditMetadataForm';

type EditMetadataFormProps = {
  form: EditMetadataFormController;
};

type SimpleMetadataFields = {
  company_name: string;
  company_overview: string;
};

export const EditMetadataForm: React.FC<EditMetadataFormProps> = ({ form }) => {
  const {
    state,
    formInstanceId,
    updateField,
    setSttKeywords,
    setCustomFields,
  } = form;

  const handleSimpleFieldsChange = (values: SimpleMetadataFields) => {
    if (values.company_name !== state.company_name) {
      updateField('company_name', values.company_name);
    }
    if (values.company_overview !== state.company_overview) {
      updateField('company_overview', values.company_overview);
    }
  };

  return (
    <div className="flex flex-col gap-y-6 w-full px-7 py-7">
      <Form<SimpleMetadataFields>
        key={formInstanceId}
        initialValue={{
          company_name: state.company_name,
          company_overview: state.company_overview,
        }}
        onChange={handleSimpleFieldsChange}
      >
        <FormItem<SimpleMetadataFields>
          name="company_name"
          label={getMetadataFieldLabel(DEFAULT_METADATA_KEYS.COMPANY_NAME)}
          inline={false}
        >
          <Input placeholder="Company Name" defaultSizeClass="w-full" />
        </FormItem>

        <FormItem<SimpleMetadataFields>
          name="company_overview"
          label={getMetadataFieldLabel(DEFAULT_METADATA_KEYS.COMPANY_OVERVIEW)}
          inline={false}
        >
          <Input
            multiline
            size="small"
            placeholder="Company Overview"
            defaultSizeClass="w-full"
          />
        </FormItem>
      </Form>

      <MetadataSectionCard
        accent
        title={getMetadataFieldLabel(DEFAULT_METADATA_KEYS.STT_KEYWORDS)}
        icon={<RecordVoiceOverOutlined fontSize="small" />}
        description="Define speech-to-text keywords your agent should recognize."
        count={state.stt_keywords.length}
        countLabel={state.stt_keywords.length === 1 ? 'keyword' : 'keywords'}
      >
        <StringList
          entries={state.stt_keywords}
          onChange={setSttKeywords}
          addLabel="Add STT Keyword"
          placeholder="Keyword"
          emptyHint="No STT keywords yet. Click below to add one."
        />
      </MetadataSectionCard>

      <MetadataSectionCard
        title="Additional Metadata"
        icon={<TuneOutlined fontSize="small" />}
        description="Add metadata fields as key-value pairs."
        count={state.custom_fields.length}
        countLabel={state.custom_fields.length === 1 ? 'field' : 'fields'}
      >
        <KeyValueList
          entries={state.custom_fields}
          onChange={setCustomFields}
          addLabel="Add Metadata Field"
          keyPlaceholder={METADATA_KEY_VALIDATION.PLACEHOLDER}
          valuePlaceholder="Field value"
          emptyHint="No additional metadata fields yet."
          lockedKeys={LOCKED_METADATA_KEYS}
          showCopyButton
        />
      </MetadataSectionCard>
    </div>
  );
};
